import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { notifyBase } from '../config';

/**
 * Embeds a Renuka Notify screen in an iframe (same-origin, via the reverse
 * proxy). Notify runs standalone; here we only surface selected routes and hand
 * it the ERP session token so the user is not asked to log in twice.
 *
 * Handshake: the embedded Notify app posts `notify-embed:request-token`; we
 * reply (and on iframe load) with `{ type: 'notify-embed:token', token }` — the
 * ERP token from localStorage. Notify exchanges it for its own JWT.
 */
const NotifyFrame = ({ path, title }) => {
  const iframeRef = useRef(null);

  const sendToken = useCallback(() => {
    const frame = iframeRef.current;
    if (!frame || !frame.contentWindow) return;
    const token = localStorage.getItem('token') || '';
    frame.contentWindow.postMessage(
      { type: 'notify-embed:token', token },
      window.location.origin
    );
  }, []);

  useEffect(() => {
    const onMessage = e => {
      if (e.origin !== window.location.origin) return;
      if (e.source !== (iframeRef.current && iframeRef.current.contentWindow))
        return;
      if (e.data && e.data.type === 'notify-embed:request-token') {
        sendToken();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [sendToken]);

  // On open (and when switching between embedded screens) bring the ERP page
  // back to the top, so the embedded content shows from its start instead of
  // inheriting the previous page's scroll position. The content scrolls inside
  // an overflow:auto container, so scroll both the window and that container.
  useEffect(() => {
    window.scrollTo(0, 0);
    if (iframeRef.current) {
      iframeRef.current.scrollIntoView({ block: 'start' });
    }
  }, [path]);

  const src = `${notifyBase}${path}${path.includes('?') ? '&' : '?'}embed=1`;

  return (
    <iframe
      ref={iframeRef}
      title={title || 'Renuka Notify'}
      src={src}
      onLoad={sendToken}
      style={{
        width: '100%',
        height: 'calc(100vh - 72px)',
        border: 'none',
        display: 'block',
      }}
    />
  );
};

NotifyFrame.propTypes = {
  path: PropTypes.string.isRequired,
  title: PropTypes.string,
};

export default NotifyFrame;
